<template>
  <hit-base-input
    :disabled="disabled"
    :full-width="adaptToContent"
    :label="baseInputLabel"
    :floating-label="useFloatingLabel"
    :inline-input="effectiveInlineInput"
    :only-show-error-border="effectiveInlineInput || useFloatingLabel"
    :row-label="rowLabel"
    :validation-state="validationState"
    :use-custom-height="useCustomHeight"
    :help="useFloatingLabel ? null : help"
    :add-bottom-margin="addBottomMargin"
  >
    <div class="h-full">
      <OnClickOutside
        class="h-full min-w-0"
        :class="{
          'w-full': (!adaptToContent || fullWidth) && !widthClass,
          'xl:w-1/2': !adaptToContent && !fullWidth && !widthClass,
          [widthClass]: !!widthClass,
        }"
        @trigger="onClickOutside"
      >
        <div
          :ref="(el) => (inputDomEl = el)"
          :class="{
            'w-full': (!adaptToContent || fullWidth) && !widthClass,
            'xl:w-1/2': !adaptToContent && !fullWidth && !widthClass,
            [widthClass]: !!widthClass,
            'border rounded': bordersVisible,
            'border-input':
              (valid && pristine) || !validationState || (valid && dirty),
            'border-danger': invalid && dirty && bordersVisible,
            'bg-input': !transparentInput,
            'bg-transparent': transparentInput,
            'cursor-not-allowed': disabled,
            'cursor-pointer': !disabled,
            'hit-input-muted-text': disabled,
            relative: useFloatingLabel,
          }"
          class="inline-block h-full text-input focus:border-input-focus focus:ring-transparent focus:outline-none"
          tabindex="0"
          @keyup.enter.stop="handleEnterClick"
          @focusin="onSelectFocusIn"
          @focusout="onSelectFocusOut"
        >
          <span
            v-if="showFloatingError"
            :class="floatingErrorLabelClassList"
            :style="floatingErrorLabelStyle"
            aria-live="polite"
          >
            <span class="hit-floating-label__text">{{
              floatingErrorMessage
            }}</span>
            <hit-floating-label-help
              v-if="floatingHelpVisible && !showFloatingCaption"
            >
              <slot name="help">
                <hit-help v-if="help" :text="help" floating />
              </slot>
            </hit-floating-label-help>
          </span>
          <div
            v-if="showFloatingCaption"
            class="hit-floating-label"
            :class="floatingLabelClassList"
          >
            <span class="hit-floating-label__text">
              {{ floatingLabelText }}
              <span
                v-if="pristine && required && invalid"
                class="text-danger-icon"
                >*</span
              >
            </span>
            <hit-floating-label-help v-if="floatingHelpVisible">
              <slot name="help">
                <hit-help v-if="help" :text="help" floating />
              </slot>
            </hit-floating-label-help>
          </div>
          <div
            :class="{
              clear: clearable,
            }"
            class="hit-select h-full"
          >
            <div
              v-if="customIcon"
              class="h-full flex items-center"
              @click.stop="toggleDropdownVisibility"
            >
              <hit-icon :color="iconColor" :icon="customIcon" clickable />
            </div>
            <div
              v-else-if="customUserName"
              class="rounded-md w-7 h-7 flex justify-center items-center text-gray-500 hover:bg-gray-100"
            >
              <div>
                {{ customUserName }}
              </div>
            </div>
            <div
              v-else
              :class="{clear: clearable, 'min-w-32': setMinWidth}"
              class="grid items-center hit-select h-full"
              @click.stop="toggleDropdownVisibility"
            >
              <div
                v-if="!multiple || (multiple && selectValue.length === 0)"
                :class="{
                  'border-padding': bordersVisible,
                }"
                class="w-full inline-grid items-center"
              >
                <div
                  :class="{
                    'text-input': value,
                    'text-input-light':
                      !value || (multiple && value.length === 0),
                    'text-transparent':
                      useFloatingLabel &&
                      !floatingLabelLifted &&
                      !hasFloatingValue,
                  }"
                  :disabled="disabled"
                  :placeholder="placeholder"
                  class="w-full hit-select-input truncate flex items-center gap-2"
                >
                  <div v-if="getItemById(value)?.icon">
                    <hit-icon :icon="getItemById(value)?.icon" />
                  </div>
                  <div v-if="getItemById(value)?.flag">
                    <hit-country-flag :country="getItemById(value).flag" />
                  </div>
                  <slot
                    v-if="value"
                    :item="
                      multiple && selectValue.length === 0
                        ? null
                        : getItemById(value)
                    "
                    :name="
                      hasSelectedItemDesignationSlot
                        ? 'selectedItemDesignation'
                        : 'itemDesignation'
                    "
                  >
                    {{ selectedItemLabel }}
                  </slot>
                  <template v-if="!value || (multiple && value.length === 0)">
                    {{ floatingPlaceholderDisplay }}
                  </template>
                </div>
              </div>

              <div
                v-if="displayCancelIcon"
                class="pr-1 inline-grid justify-center items-center cursor-pointer"
              >
                <hit-icon
                  class="text-input outline-none focus:outline-none cursor-pointer"
                  icon="clear"
                  :clickable="true"
                  @click.stop="clearInput"
                />
              </div>
              <div
                v-if="multiple && selectValue.length > 0"
                class="w-full flex flex-wrap items-center gap-1 py-1"
                :class="{'border-padding': bordersVisible}"
              >
                <div
                  v-for="selectVal of selectValue"
                  :key="selectVal"
                  :style="{
                    'background-color': `${getItemBackground(selectVal)}`,
                    color: `${getItemFontColour(selectVal)}`,
                  }"
                  class="flex-row flex font-medium px-1.5 rounded-full h-5 items-center"
                >
                  <div
                    class="px-1 text-xs font-normal leading-none max-w-full grid grid-flow-col gap-2"
                  >
                    <div v-if="getItemById(value)?.icon">
                      <hit-icon :icon="getItemById(value).icon" size="sm" />
                    </div>
                    <div v-if="getItemById(selectVal)?.flag">
                      <hit-country-flag
                        :country="getItemById(selectVal)?.flag"
                      />
                    </div>
                    <slot :item="getItemById(selectVal)" name="itemDesignation">
                      {{ getItemLabel(getItemById(selectVal)) }}
                    </slot>
                  </div>
                  <div class="inline-grid items-center">
                    <hit-icon
                      v-if="itemsRemovable"
                      :clickable="!disabled"
                      class="outline-none focus:outline-none ml-1"
                      color="panel"
                      icon="clear"
                      size="xs"
                      @click.stop="onSelect(selectVal)"
                    />
                  </div>
                </div>
              </div>
              <div
                :class="{
                  'border-l border-input': divisionLine,
                }"
                class="h-full w-8 pl-1 pr-1 text-center inline-grid items-center"
              >
                <hit-icon
                  :icon="isDropdownVisible ? 'expand-top' : 'expand-bottom'"
                  class="w-6 h-6 outline-none focus:outline-none inline-grid justify-center items-center"
                />
              </div>
            </div>
            <div class="relative">
              <Teleport to="#root-element">
                <div
                  v-if="isDropdownVisible"
                  data-hit-select-dropdown
                  :style="dropdownStyle"
                  class="absolute shadow rounded z-40 inline-block w-auto bg-input transition-on-open"
                  @keydown.down.prevent="incrementPreselectedItem"
                  @keydown.up.prevent="decrementPreselectedItem"
                  @keyup.enter="handleEnterClick"
                >
                  <div v-if="!currentCustomAction" class="flex flex-col w-full">
                    <div
                      v-if="filterable"
                      class="w-full border-input rounded-t border-b hover:bg-table-hover cursor-pointer"
                    >
                      <div
                        class="flex w-full items-center p-2 pl-2 border-none relative"
                      >
                        <div class="w-full items-center flex-auto rounded">
                          <hit-search
                            :ref="(ref) => (hitSearchRef = ref)"
                            :class="searchWidth"
                            :placeholder="t('hit-components.common.search')"
                            :value="searchValue"
                            @input="filter"
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="noItemsDropdown === 0"
                      class="w-full border-gray-100 rounded-t"
                    >
                      <div
                        class="flex w-full items-center p-2 pl-2 border-none bg-input relative"
                      >
                        <div class="w-full items-center flex">
                          <div class="mx-2 leading-6">
                            <span v-if="!optionsLoading">
                              {{ noMatchingResultText }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-for="(item, itemId) in itemsToDisplaySorted"
                    :id="'select-item-' + getItemId(item, itemId)"
                    :key="getItemId(item, itemId)"
                    class="w-full border-gray-100 text-input select-item cursor-pointer"
                    @click="onSelect(getItemId(item, itemId), item)"
                    @mouseover="resetPreselectedItem"
                  >
                    <div
                      :class="{
                        'border-input-focus': isItemSelected(
                          getItemId(item, itemId),
                        ),
                        'border-none': !isItemSelected(getItemId(item, itemId)),
                        'bg-table-hover': isItemPreselectedViaTab(
                          getItemId(item, itemId),
                        ),
                      }"
                      class="flex w-full items-center p-2 pl-2 bg-input border-l-2 relative hover:bg-table-hover hover:border-input-focus hit-select-item"
                    >
                      <div class="w-full items-center flex">
                        <div
                          class="mx-2 leading-6 grid grid-flow-col gap-2 break-words whitespace-normal"
                        >
                          <hit-icon
                            v-if="item?.icon"
                            :icon="item.icon"
                            size="sm"
                          />
                          <hit-country-flag
                            v-if="item.flag"
                            :country="item.flag"
                          />
                          <slot :item="item" name="itemDesignation">
                            {{ getItemLabel(item, true) }}
                          </slot>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-for="customAction of customActions"
                    :key="customAction.buttonLabel"
                    class="w-full border-gray-100 rounded-t border-t cursor-pointer"
                    @click.stop="showCustomActionSlot(customAction)"
                  >
                    <div
                      class="flex w-full items-center p-2 pl-2 border-transparent border-l-2 relative"
                    >
                      <div class="w-full items-center flex">
                        <hit-icon
                          v-if="customAction.buttonIcon"
                          :icon="customAction.buttonIcon"
                        />
                        <div class="mx-2 leading-6">
                          {{ customAction.buttonLabel }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Teleport>
              <!-- stop click propagation (do not trigger click-outside) -->
              <div
                v-if="currentCustomAction"
                class="inline-grid w-full creation-menu bg-input gap-1"
                @click.stop
              >
                <div
                  class="custom-action-header grid gap-1 border-b items-center p-1 h-12 border-gray-100"
                >
                  <hit-icon
                    :clickable="true"
                    icon="back"
                    @click.stop="hideCustomActionSlot()"
                  />
                  <span>{{ currentCustomAction.buttonLabel }}</span>
                </div>
                <div class="custom-action-body p-1">
                  <slot
                    :close-dropdown-fn="closeDropdown"
                    :close-slot-fn="hideCustomActionSlot"
                    :name="currentCustomAction.id"
                    :select-fn="onSelect"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </OnClickOutside>
    </div>
    <template v-if="!useFloatingLabel" #help>
      <!-- @slot Help of the select -->
      <slot name="help" />
    </template>
  </hit-base-input>
</template>

<script>
import HitFormValidationMixin from '../../../mixins/form/HitFormValidationMixin';
import TailwindUtils from '../../../utils/tailwind/tailwind';
import HitSelectMixin from '../../../mixins/form/HitSelectMixin';
import HitFloatingLabelMixin from '../../../mixins/form/HitFloatingLabelMixin';
import {useI18n} from 'vue-i18n';
import HitBaseInput from '../input/HitBaseInput.vue';
import HitIcon from '../../icon/HitIcon.vue';
import HitHelp from '../../help/HitHelp.vue';
import HitFloatingLabelHelp from './HitFloatingLabelHelp.vue';
import HitSearch from '../../search/HitSearch.vue';
import {OnClickOutside} from '@vueuse/components';
import {defineAsyncComponent, nextTick} from 'vue';
import {HitBreakpointsMixin} from '../../../mixins';
import {ColourUtils} from '../../../utils/colour';

export default {
  name: 'HitSelect',
  components: {
    HitBaseInput,
    HitIcon,
    HitHelp,
    HitFloatingLabelHelp,
    HitSearch,
    OnClickOutside,
    HitCountryFlag: defineAsyncComponent(
      () => import('../../icon/HitCountryFlag.vue'),
    ),
  },
  mixins: [
    HitSelectMixin,
    HitFormValidationMixin,
    HitFloatingLabelMixin,
    HitBreakpointsMixin,
  ],
  props: {
    /**
     * Function to get the item label
     */
    getLabelFromItem: {
      type: Function,
      default: (item) => {
        if (item.label) {
          return item.label;
        } else {
          return item;
        }
      },
    },
    selectionType: {
      type: String,
      default: '',
    },
    sortConfig: {
      type: Object,
      required: false,
      default: null,
    },
    optionsToExclude: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * Name of the labels where we want to apply the fallback => display user language if defined and otherwise display main language
     */
    fallBackConfig: {
      type: Object,
      required: false,
      default: () => {
        return {
          fallBackLabels: [],
          userLanguage: 'en_gb',
          mainLanguage: 'en_gb',
        };
      },
    },
    customIcon: {
      type: String,
      required: false,
      default: null,
    },
    customUserName: {
      type: String,
      required: false,
      default: null,
    },
    instantFocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    iconColor: {
      type: String,
      required: false,
      default: 'card',
    },
    transparentInput: {
      type: Boolean,
      required: false,
      default: false,
    },
    useCustomHeight: {
      type: Boolean,
      required: false,
      default: true,
    },
    setMinWidth: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemsRemovable: {
      type: Boolean,
      required: false,
      default: true,
    },
    help: {
      type: String,
      required: false,
      default: null,
    },
    widthClass: {
      type: String,
      default: null,
    },
  },
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      items: [],
      isDropdownVisible: false,
      searchValue: '',
      displayCreation: false,
      formData: null,
      currentCustomAction: null,
      noMatchingResultText: '',
      inputDomEl: null,
      hitSearchRef: null,
      inputBottomY: 0,
      inputTopY: 0,
      inputLeftX: 0,
      inputRightX: 0,
      tabPreselectedItemIndex: null,
      maxDropdownHeight: 250,
      itemHeight: 40,
      searchBarHeight: 50,
      selectHasFocus: false,
    };
  },
  created() {
    const translate =
      typeof this.t === 'function'
        ? this.t
        : typeof this.$t === 'function'
          ? this.$t
          : (key) => key;
    this.noMatchingResultText = translate(
      'hit-components.common.no-matching-result',
    );
  },
  computed: {
    useFloatingLabel() {
      return this.floatingLabel && !!this.label && !this.effectiveInlineInput;
    },
    floatingHelpVisible() {
      return !!this.$slots.help || !!this.help;
    },
    hasFloatingValue() {
      if (this.multiple) {
        return Array.isArray(this.value) && this.value.length > 0;
      }
      return (
        this.value !== null && this.value !== undefined && this.value !== ''
      );
    },
    floatingPlaceholderDisplay() {
      if (!this.useFloatingLabel) {
        return this.placeholder;
      }
      if (!this.floatingLabelLifted) {
        return '';
      }
      return this.placeholder;
    },
    hasSelectedItemDesignationSlot() {
      return !!this.$slots['selectedItemDesignation'];
    },
    noItemsDropdown() {
      if (this.itemsToDisplay) {
        return Object.keys(this.itemsToDisplay).length;
      }
      return 0;
    },
    selectValue() {
      if (this.multiple) {
        if (!Array.isArray(this.value) || this.value.length === 0) {
          return [];
        }
        const itemsSource = Array.isArray(this.itemsToDisplay)
          ? this.itemsToDisplay
          : Object.values(this.itemsToDisplay || {});
        const itemsToDisplayIds = itemsSource.map((item) =>
          this.getItemId(item, null),
        );
        return this.value.filter((item) => itemsToDisplayIds.includes(item));
      }
      if (this.value) {
        return this.value;
      }
      return '';
    },
    displayCancelIcon() {
      return (
        !this.disabled &&
        this.selectValue !== '' &&
        !this.multiple &&
        this.clearable &&
        this.selectedItemLabel !== ''
      );
    },
    itemsToDisplay() {
      if (this.customFilter || !this.filterable) {
        return this.options;
      } else {
        let splitSearchValue = (this.searchValue || '')
          .split(' ')
          .map((elt) => elt.toLowerCase());
        if (Array.isArray(this.options)) {
          return this.options.filter((elt) => {
            return splitSearchValue.every((searchVal) => {
              const labelText = String(
                this.getItemLabel(elt) ?? '',
              ).toLowerCase();
              return labelText.includes(searchVal);
            });
          });
        } else {
          return Object.keys(this.options)
            .filter((key) => {
              return splitSearchValue.every((searchVal) => {
                const labelText = String(
                  this.getItemLabel(this.options[key]) ?? '',
                ).toLowerCase();
                return labelText.includes(searchVal);
              });
            })
            .reduce((obj, key) => {
              obj[key] = this.options[key];
              return obj;
            }, {});
        }
      }
    },
    itemsToDisplaySorted() {
      if (!this.sortConfig) {
        return this.itemsToDisplay.filter(
          (item) => !this.optionsToExclude.includes(item.id),
        );
      }
      let field = this.sortConfig.field;
      let order = this.sortConfig.order === 'asc';
      let result = this.itemsToDisplay;
      result.sort((item1, item2) => {
        if (!item1[field] && !item2[field]) {
          return 0;
        } else if (!item1[field]) {
          return -1;
        } else if (!item2[field]) {
          return 1;
        }
        return order
          ? item1[field].localeCompare(item2[field])
          : item2[field].localeCompare(item1[field]);
      });
      return result.filter((item) => !this.optionsToExclude.includes(item.id));
    },
    selectedItemLabel() {
      return this.multiple || this.loading
        ? ''
        : this.getItemLabel(this.getItemById(this.selectValue));
    },
    searchWidth() {
      let values = (
        Array.isArray(this.options) ? this.options : Object.values(this.options)
      ).map((elt) => String(this.getItemLabel(elt) ?? ''));

      let longestWord = values.reduce((longest, currentWord) => {
        if (currentWord.length > longest.length) {
          return currentWord;
        } else {
          return longest;
        }
      }, '');

      if (this.noItemsDropdown === 0) {
        longestWord = this.noMatchingResultText;
      }

      return TailwindUtils.getWidthFromWord(longestWord);
    },
    dropdownStyle() {
      let result = {};
      if (!this.inputDomEl) {
        return {
          left: '0px',
          top: '0px',
          height: '0px',
          overflow: 'hidden',
        };
      }

      let spaceBetweenInputRightSideAndRightScreenEdge =
        window.innerWidth - this.inputRightX;
      let spaceBetweenInputLeftSideAndRightScreenEdge =
        window.innerWidth - this.inputLeftX;
      let spaceBetweenInputRightSideAndLeftScreenEdge = this.inputRightX;
      // width of dropdown should be around 200-250 but if one element is long it might be more, so it's better to account
      // for 300 I think. Any larger is a problem anyway since the design should work for a minimum screen width of 320
      let enoughSpaceLeftAligned =
        spaceBetweenInputLeftSideAndRightScreenEdge > 300;
      let enoughSpaceRightAligned =
        spaceBetweenInputRightSideAndLeftScreenEdge > 300;

      let alignDropDownRight =
        !enoughSpaceLeftAligned &&
        (enoughSpaceRightAligned ||
          spaceBetweenInputRightSideAndLeftScreenEdge >
            spaceBetweenInputLeftSideAndRightScreenEdge);

      if (alignDropDownRight) {
        result.right = spaceBetweenInputRightSideAndRightScreenEdge + 'px';
      } else {
        result.left = this.inputLeftX + 'px';
      }

      const customActionCount = this.customActions?.length ?? 0;
      const visibleItemCount =
        Math.max(this.noItemsDropdown, 1) + customActionCount;
      const extraHeight = this.filterable ? this.searchBarHeight : 0;

      const viewportHeight =
        window.visualViewport?.height ?? window.innerHeight;
      const availableSpaceBelow = viewportHeight - this.inputBottomY - 16;
      const availableSpaceAbove = this.inputTopY - 16;
      const calculatedHeight =
        Math.max(visibleItemCount, 1) * this.itemHeight + extraHeight;
      const optimalHeight = Math.min(calculatedHeight, this.maxDropdownHeight);

      result.overflowY = 'auto';
      let maxHeight;
      if (
        availableSpaceBelow >= optimalHeight ||
        availableSpaceBelow >= availableSpaceAbove
      ) {
        result.top = this.inputBottomY + 'px';
        maxHeight = Math.min(
          Math.max(availableSpaceBelow, 40),
          this.maxDropdownHeight,
        );
      } else {
        maxHeight = Math.min(
          Math.max(availableSpaceAbove, 40),
          this.maxDropdownHeight,
        );
        result.bottom = viewportHeight - this.inputTopY + 'px';
      }
      result.maxHeight = maxHeight + 'px';

      return result;
    },
  },
  methods: {
    hideCustomActionSlot() {
      this.showCustomActionSlot(undefined);
    },
    showCustomActionSlot(customAction) {
      if (this.currentCustomAction) {
        /**
         * When a custom action slot is hidden
         */
        this.$emit(`custom-slot-${this.currentCustomAction.id}:hide`);
      }
      if (customAction) {
        /**
         * When a custom slot is shown
         */
        this.$emit(`custom-slot-${customAction.id}:show`);
      }
      this.currentCustomAction = customAction;
    },
    getItemByIdFrom(id, items) {
      let result;
      if (Array.isArray(items)) {
        result = items.find((x) => {
          if (x === null) {
            return false;
          }
          return x[this.itemIdProperty ? this.itemIdProperty : 'id'] === id;
        });
      } else {
        result = items[id];
      }
      return result;
    },
    getItemById(id) {
      let result = undefined;
      if (id) {
        result = this.getItemByIdFrom(id, this.options);
        if (!result && this.selectedOptions) {
          result = this.getItemByIdFrom(id, this.selectedOptions);
        }
        if (!result && !this.loading && !this.optionsLoading) {
          // console.error(
          //   `Error in getItemById `,
          //   id,
          //   this.options,
          //   this.selectedOptions
          // );
        }
      }

      return result;
    },
    onChange(value) {
      if (this.validationState) {
        this.validationState.$touch();
      }
      this.fireInputChange(value);
    },
    onClickOutside(event) {
      // Dropdown is Teleported to #root-element, so it is not under this wrapper:
      // ignore any click that still belongs to this select’s dropdown panel.
      try {
        if (event.target?.closest?.('[data-hit-select-dropdown]')) {
          return;
        }
        this.closeDropdown();
      } catch {
        this.closeDropdown();
      }
    },
    onSelectFocusIn() {
      this.selectHasFocus = true;
    },
    onSelectFocusOut() {
      this.$nextTick(() => {
        const el = this.inputDomEl;
        if (!el) {
          this.selectHasFocus = false;
          return;
        }
        const active = document.activeElement;
        if (active && el.contains(active)) {
          return;
        }
        if (this.isDropdownVisible) {
          return;
        }
        this.selectHasFocus = false;
      });
    },
    clearInput() {
      this.$emit('clear');
      this.onChange(null);
    },
    updateInputPosition() {
      if (this.inputDomEl) {
        let rect = this.inputDomEl.getBoundingClientRect();
        if (
          rect.bottom !== 0 &&
          rect.top !== 0 &&
          rect.left !== 0 &&
          rect.right !== 0
        ) {
          this.inputBottomY = rect.bottom;
          this.inputTopY = rect.top;
          this.inputLeftX = rect.left;
          this.inputRightX = rect.right;
        }
      }
    },
    closeDropdown() {
      this.isDropdownVisible = false;
      this.hideCustomActionSlot();
    },
    updateFilter() {
      if (!this.isDropdownVisible) {
        this.filter(null);
      } else {
        this.updateInputPosition();
        this.$emit('show-options');
        nextTick(() => {
          if (
            this.hitSearchRef &&
            this.hitSearchRef.inputDomEl &&
            this.responsiveBreakpointSm
          ) {
            this.hitSearchRef.inputDomEl.focus();
          }
        });
      }
    },
    toggleDropdownVisibility() {
      if (!this.disabled) {
        this.isDropdownVisible = !this.isDropdownVisible;
        if (this.isDropdownVisible) {
          this.updateInputPosition();
        }
      }
    },
    filter(value) {
      this.resetPreselectedItem();
      if (value != this.searchValue) {
        this.searchValue = value;
        /**
         * When the options are filtered
         */
        this.$emit('filter', value);
      }
    },
    isItemSelected(value) {
      return this.multiple
        ? Array.isArray(this.value) && this.value.includes(value)
        : this.value === value;
    },
    isItemPreselectedViaTab(value) {
      if (this.tabPreselectedItemIndex === null) {
        return false;
      }
      const pre = this.itemsToDisplaySorted[this.tabPreselectedItemIndex];
      return value === this.getItemId(pre, this.tabPreselectedItemIndex);
    },
    onSelect(itemId, item = null) {
      if (!this.disabled) {
        if (
          (this.multiple && !this.selectValue.includes(itemId)) ||
          !this.multiple
        ) {
          this.selectItem(itemId, item);
        } else {
          this.unselectItem(itemId, item);
        }
      }
      this.closeDropdown();
    },
    selectItem(itemId, _item = null) {
      let result = itemId;
      if (this.multiple) {
        const current = Array.isArray(this.value) ? [...this.value] : [];
        if (!current.includes(itemId)) {
          current.push(itemId);
        }
        result = current;
      } else {
        this.isDropdownVisible = false;
      }
      this.onChange(result);
      this.$emit('customHitSelectSelect', itemId);
      this.$emit('add', itemId, this.getItemById(itemId));
    },
    unselectItem(itemId, _item = null) {
      const current = Array.isArray(this.value) ? this.value : this.selectValue;
      const result = current.filter((elt) => elt !== itemId);
      this.onChange(result);
      this.$emit('remove', itemId, this.getItemById(itemId));
    },
    getItemLabel(item, dropdown) {
      if (item) {
        if (dropdown && this.dropdownLabelFields.length > 0) {
          return this.dropdownLabelFields
            .map((elt) => item[elt])
            .join(this.labelSeparator);
        } else if (this.labelFields.length > 0) {
          const localisedLabels = [];
          const handledFallbacks = [];
          this.labelFields.forEach((l) => {
            if (
              l[l.length - 3] === '_' &&
              l[l.length - 6] === '_' &&
              this.fallBackConfig.fallBackLabels.includes(l.split('_')[0])
            ) {
              if (!handledFallbacks.includes(l.split('_')[0])) {
                const userLanguageKey =
                  l.split('_')[0] + '_' + this.fallBackConfig.userLanguage;
                const mainLanguageKey =
                  l.split('_')[0] + '_' + this.fallBackConfig.mainLanguage;
                localisedLabels.push(
                  item[userLanguageKey] ? userLanguageKey : mainLanguageKey,
                );
                handledFallbacks.push(l.split('_')[0]);
              }
            } else {
              localisedLabels.push(l);
            }
          });
          return localisedLabels
            .map((elt) => item[elt])
            .join(this.labelSeparator);
        } else {
          if (item.label) {
            const raw = this.getLabelFromItem(item.label);
            return raw == null ? '' : String(raw);
          }
          const raw = this.getLabelFromItem(item);
          if (raw == null) return '';
          if (typeof raw !== 'object') return String(raw);
          if (typeof raw.fullDesignation === 'string') {
            return raw.fullDesignation;
          }
          if (typeof raw.fastModeBrowserDesignation === 'string') {
            return raw.fastModeBrowserDesignation;
          }
          if (typeof raw.designation === 'string') return raw.designation;
          if (typeof raw.name === 'string') return raw.name;
          return raw.id != null ? String(raw.id) : '';
        }
      } else {
        return '';
      }
    },
    getItemBackground(selectedValue) {
      const item = this.getItemById(selectedValue) || {};
      const {colour} = item;
      return colour ? `#${colour}` : 'var(--color-tag)';
    },
    getItemFontColour(selectedValue) {
      const bgColor = this.getItemBackground(selectedValue);
      return ColourUtils.getFontColorOnBg(bgColor);
    },
    incrementPreselectedItem() {
      if (this.tabPreselectedItemIndex === null) {
        this.tabPreselectedItemIndex = 0;
      } else {
        this.tabPreselectedItemIndex =
          (this.tabPreselectedItemIndex + 1) % this.itemsToDisplaySorted.length;
      }
      this.scrollToPreselectedItem();
    },
    decrementPreselectedItem() {
      if (!this.tabPreselectedItemIndex) {
        this.tabPreselectedItemIndex = this.itemsToDisplaySorted.length - 1;
      } else {
        this.tabPreselectedItemIndex--;
      }
      this.scrollToPreselectedItem();
    },
    resetPreselectedItem() {
      this.tabPreselectedItemIndex = null;
    },
    scrollToPreselectedItem() {
      const itemToScrollTo =
        this.tabPreselectedItemIndex === null
          ? this.itemsToDisplaySorted[0]
          : this.itemsToDisplaySorted[this.tabPreselectedItemIndex];
      this.$nextTick(() => {
        let element = document.getElementById(
          'select-item-' + this.getItemId(itemToScrollTo, null),
        );
        if (element) {
          const bounding = element.getBoundingClientRect();
          if (bounding.top < 0 || bounding.bottom > window.innerHeight) {
            this.$nextTick(() => {
              element.scrollIntoView();
            });
          }
        }
      });
    },
    handleEnterClick() {
      if (!this.isDropdownVisible) {
        this.isDropdownVisible = true;
        return;
      }
      if (this.tabPreselectedItemIndex !== null) {
        const pre = this.itemsToDisplaySorted[this.tabPreselectedItemIndex];
        this.onSelect(this.getItemId(pre, this.tabPreselectedItemIndex), pre);
      }
      this.tabPreselectedItemIndex = null;
      this.isDropdownVisible = false;
      // this.inputDomEl.focus();
    },
    /**
     * Close the dropdown when the page/table is scrolled, but keep it open
     * when the user scrolls the dropdown list itself.
     */
    isScrollInsideDropdown(event) {
      try {
        return !!event?.target?.closest?.('[data-hit-select-dropdown]');
      } catch {
        return false;
      }
    },
    closeDropdownOnScroll(event) {
      if (!this.isDropdownVisible) {
        return;
      }
      if (this.isScrollInsideDropdown(event)) {
        return;
      }
      this.closeDropdown();
    },
    bindCloseOnScrollListeners() {
      window.addEventListener('scroll', this.closeDropdownOnScroll, true);
      window.addEventListener('wheel', this.closeDropdownOnScroll, {
        capture: true,
        passive: true,
      });
    },
    unbindCloseOnScrollListeners() {
      window.removeEventListener('scroll', this.closeDropdownOnScroll, true);
      window.removeEventListener('wheel', this.closeDropdownOnScroll, {
        capture: true,
      });
    },
  },
  mounted() {
    if (this.instantFocus && this.inputDomEl) {
      this.inputDomEl.focus();
    }
  },
  beforeUnmount() {
    this.unbindCloseOnScrollListeners();
  },
  watch: {
    isDropdownVisible(newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.bindCloseOnScrollListeners();
        setTimeout(this.updateInputPosition, 1000);
      } else if (newValue === false && oldValue === true) {
        this.unbindCloseOnScrollListeners();
      }
      if (!newValue && this.inputDomEl) {
        this.inputDomEl.focus();
      }
      setTimeout(() => {
        this.updateFilter();
      }, 75);
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.hit-select {
  grid-template-columns: 1fr max-content;

  &.clear {
    grid-template-columns: 1fr max-content max-content;
  }
}

.creation-menu {
  grid-template-areas:
    'custom-action-header'
    'custom-action-body';
  grid-template-rows: max-content 1fr;
}

.custom-action-header {
  //grid-template: custom-action-header;
  grid-template-columns: max-content 1fr;
}

.custom-action-body {
  //grid-template: custom-action-body;
  grid-template-rows: 1fr max-content;
}
</style>

<style lang="scss">
.hit-select-item {
  a:focus,
  a:hover {
    color: inherit;
  }
}

.border-padding {
  padding-right: 7px;
  padding-left: 7px;
}

.transition-on-open {
  transition: max-height 0.3s ease-in-out;
}
</style>
